
import { client, getSanityEditLink } from "./sanity-client";

// Page-scoped navbar transparency handler — mirrors the pattern used by
// every other transparent-navbar page (home-redesign, storage, cloud,
// etc.). The shared navbar.js handler queries by .navbar-v3-transparent
// which self-destructs once the class is removed; querying .navbar
// directly keeps the toggle working in both directions.
function handleNavbarScroll() {
  const navbar = document.querySelector(".navbar");
  if (!navbar) return;

  if (window.scrollY > 50) {
    navbar.classList.add("navbar-v3-scrolled");
    navbar.classList.remove("navbar-v3-transparent");
  } else {
    navbar.classList.remove("navbar-v3-scrolled");
    navbar.classList.add("navbar-v3-transparent");
  }
}

window.addEventListener("scroll", handleNavbarScroll, { passive: true });
window.addEventListener("load", handleNavbarScroll);

document.addEventListener("DOMContentLoaded", async () => {
  const root = document.querySelector("main.lookingfor-page");
  if (!root) return;

  handleNavbarScroll();

  try {
    const query = `*[_type == "lookingFor"][0]{
      _id,
      showHero,
      showNotice,
      showTeam,
      showWork,
      showCta,
      heroEyebrow,
      pageTitle,
      heroDescription,
      notice,
      qualitiesSection{
        ...,
        values[]{..., _key}
      },
      proficienciesSection{
        ...,
        skills[]{..., _key}
      },
      careerSection
    }`;
    const data = await client.fetch(query);
    if (!data) {
      console.error("Looking For data not found");
      return;
    }

    renderHero(data);
    renderNotice(data);
    renderTeam(data);
    renderWork(data);
    renderCta(data);
  } catch (error) {
    console.error("Error fetching Looking For data:", error);
  }
});

// ── Hero ──────────────────────────────────────────────────────────────
function renderHero(data) {
  const section = document.getElementById("lookingfor-hero-section");
  if (!section) return;

  if (data.showHero === false) {
    section.style.display = "none";
    return;
  }

  setText("lookingfor-hero-eyebrow", data.heroEyebrow);
  setText("lookingfor-hero-lede", data.heroDescription);

  // Title gets a trailing red dot via the .lf-hero__dot span — keep the
  // existing dot in the HTML and only replace the leading text node.
  const titleEl = document.getElementById("lookingfor-hero-title");
  if (titleEl && data.pageTitle) {
    const dot = titleEl.querySelector(".lf-hero__dot");
    titleEl.textContent = data.pageTitle.replace(/\.$/, "");
    if (dot) titleEl.appendChild(dot);
  }

  if (data._id) {
    insertEditLinkBefore(titleEl, data._id, "heroSection", "Edit Hero");
  }
}

// ── Recruitment notice (pinned hero card) ─────────────────────────────
function renderNotice(data) {
  const notice = document.getElementById("lookingfor-notice");
  if (!notice) return;

  if (data.showNotice === false) {
    notice.style.display = "none";
    return;
  }

  const n = data.notice || {};

  setText("lookingfor-notice-marker", n.marker);
  setText("lookingfor-notice-title", n.title);
  setText("lookingfor-notice-p1", n.paragraph1);

  const emphEl = document.getElementById("lookingfor-notice-p1-emphasis");
  if (emphEl && n.paragraph1Emphasis) emphEl.textContent = " " + n.paragraph1Emphasis;

  // Paragraph 2: bold any literal "@coriolis.co.in" occurrence.
  const p2El = document.getElementById("lookingfor-notice-p2");
  if (p2El && n.paragraph2) {
    p2El.innerHTML = escapeHtml(n.paragraph2)
      .replace(/(@coriolis\.co\.in)/g, "<b>$1</b>");
  }

  // Paragraph 3: linkify any email → mailto, prefer the explicit contactEmail
  // when supplied as the link target.
  const p3El = document.getElementById("lookingfor-notice-p3");
  if (p3El && n.paragraph3) {
    p3El.innerHTML = linkifyEmails(escapeHtml(n.paragraph3), n.contactEmail);
  }

  if (data._id) {
    insertEditLinkBefore(notice, data._id, "notice", "Edit Recruitment Notice");
  }
}

// ── "The team" section ────────────────────────────────────────────────
function renderTeam(data) {
  const section = document.getElementById("lookingfor-team-section");
  if (!section) return;

  if (data.showTeam === false) {
    section.style.display = "none";
    return;
  }

  const team = data.qualitiesSection || {};
  setText("lookingfor-team-label", team.label);
  setText("lookingfor-team-heading", team.title);
  setText("lookingfor-team-intro", team.description);

  const listEl = document.getElementById("lookingfor-qualities-list");
  if (listEl && Array.isArray(team.values) && team.values.length) {
    listEl.innerHTML = team.values.map((q, i) => `
      <div class="lf-quality">
        ${data._id && q._key
          ? getSanityEditLink(data._id, "lookingFor", `qualitiesSection.values[_key=="${q._key}"]`, "Edit Quality")
          : ""}
        <span class="lf-quality__n">${pad2(i + 1)}</span>
        <h4>${escapeHtml(q.title || "")}</h4>
        <ul>
          ${(q.items || []).map((it) => `<li>${escapeHtml(it)}</li>`).join("")}
        </ul>
      </div>
    `).join("");
  }

  if (data._id) {
    const heading = document.getElementById("lookingfor-team-heading");
    insertEditLinkBefore(heading, data._id, "qualitiesSection", "Edit Team Section");
  }
}

// ── "The work" section ────────────────────────────────────────────────
function renderWork(data) {
  const section = document.getElementById("lookingfor-work-section");
  if (!section) return;

  if (data.showWork === false) {
    section.style.display = "none";
    return;
  }

  const work = data.proficienciesSection || {};
  setText("lookingfor-work-label", work.label);
  setText("lookingfor-work-heading", work.title);
  setText("lookingfor-work-subhead", work.subtitle);

  const listEl = document.getElementById("lookingfor-tech-list");
  if (listEl && Array.isArray(work.skills) && work.skills.length) {
    listEl.innerHTML = work.skills.map((g) => `
      <div class="lf-tech-col">
        ${data._id && g._key
          ? getSanityEditLink(data._id, "lookingFor", `proficienciesSection.skills[_key=="${g._key}"]`, "Edit Column")
          : ""}
        <h5>${escapeHtml(g.title || "")}</h5>
        <ul>
          ${(g.items || []).map((it) => `<li>${escapeHtml(it)}</li>`).join("")}
        </ul>
      </div>
    `).join("");
  }

  if (data._id) {
    const heading = document.getElementById("lookingfor-work-heading");
    insertEditLinkBefore(heading, data._id, "proficienciesSection", "Edit Work Section");
  }
}

// ── Contact CTA ───────────────────────────────────────────────────────
function renderCta(data) {
  const section = document.getElementById("lookingfor-cta-section");
  if (!section) return;

  if (data.showCta === false) {
    section.style.display = "none";
    return;
  }

  const cta = data.careerSection || {};
  // Legacy compatibility — old field still hides the section if explicitly false.
  if (cta.showCareerSection === false) {
    section.style.display = "none";
    return;
  }

  setText("lookingfor-cta-eyebrow", cta.eyebrow);
  setText("lookingfor-cta-text", cta.text);
  setText("lookingfor-cta-small", cta.small);

  const emailEl = document.getElementById("lookingfor-cta-email");
  if (emailEl && cta.email) {
    emailEl.textContent = cta.email;
    emailEl.href = `mailto:${cta.email}`;
  }

  if (data._id) {
    const inner = section.querySelector(".lf-cta__inner");
    if (inner) {
      inner.classList.add("position-relative");
      inner.insertAdjacentHTML(
        "afterbegin",
        getSanityEditLink(data._id, "lookingFor", "careerSection", "Edit Contact CTA")
      );
    }
  }
}

// ── Helpers ───────────────────────────────────────────────────────────
function setText(id, value) {
  if (value == null) return;
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

function insertEditLinkBefore(el, docId, group, label) {
  if (!el) return;
  el.parentElement.classList.add("position-relative");
  el.insertAdjacentHTML("beforebegin", getSanityEditLink(docId, "lookingFor", group, label));
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function linkifyEmails(escapedText, preferredEmail) {
  return escapedText.replace(/([\w.+-]+@[\w.-]+\.\w+)/g, (match) => {
    const href = preferredEmail || match;
    return `<a href="mailto:${href}">${match}</a>`;
  });
}

function pad2(n) {
  return n < 10 ? `0${n}` : String(n);
}
